/**
 * Product Success Story Dashboard
 *
 * Client-facing page showing the complete success story of a product including:
 * - Before/after content comparison
 * - SERP position evolution
 * - AEO benchmarking improvements
 * - Semantic relevance to common questions
 * - Conversation discovery across platforms
 *
 * Reference: docs/delivery/stories/79/prd.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Loader2,
  ExternalLink,
  Share2,
  Download,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  Target,
  BarChart3,
  Clock,
  CheckCircle,
  AlertCircle,
  Copy,
  Calendar,
  Users,
  Sparkles,
  Bot,
  Eye,
  RefreshCw,
  ArrowRight,
  Search,
  FileQuestion,
  Zap,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { SemanticGraph } from '@/components/success-story/SemanticGraph';

// Types
interface Product {
  id: string;
  name: string;
  category: string;
  url: string;
  siteId: string;
  siteName: string;
  siteDomain: string;
  successStoryEnabled: boolean;
  publishedAt: string | null;
  contentDeployedAt: string | null;
}

interface ConversationThread {
  id: string;
  text: string;
  source_url: string;
  source_subreddit: string | null;
  source_timestamp: string;
  score: number | null;
  platform: string;
  sentiment: 'positive' | 'neutral' | 'negative';
}

interface ConversationsData {
  threads: ConversationThread[];
  totalCount: number;
  sentimentSummary: {
    positive: number;
    neutral: number;
    negative: number;
    positivePercent: number;
    neutralPercent: number;
    negativePercent: number;
  };
  platforms: Record<string, { count: number; threads: ConversationThread[] }>;
}

interface SemanticQuestion {
  id: string;
  text: string;
  position: { x: number; y: number };
  similarity: number;
  status: 'answered' | 'partial' | 'gap';
  searchVolume: number | null;
  category: string | null;
}

interface SemanticGraphData {
  productPosition: { x: number; y: number };
  productName: string;
  questions: SemanticQuestion[];
  summary: {
    answered: number;
    partial: number;
    gap: number;
    answeredPercent: number;
    partialPercent: number;
    gapPercent: number;
  };
}

interface TimelineEvent {
  id: string;
  event_type: string;
  event_date: string;
  title: string;
  description: string;
  metadata: Record<string, unknown>;
}

interface AEOMetrics {
  hasAEO: boolean;
  generationId?: string;
  benchmarkScore?: number;
  qualityMetrics?: Record<string, number>;
  generatedAt?: string;
  status?: string;
}

interface LLMVisibilityCheck {
  id: string;
  llm_provider: string;
  llm_model: string;
  query_type: string;
  query_strength?: number;
  query_text: string;
  site_mentioned: boolean;
  product_mentioned: boolean;
  mention_position: number | null;
  citation_quality: number | null;
  accuracy_score: number | null;
  competitors_mentioned: string[] | null;
  checked_at: string;
}

// Computed metrics for Citation vs Mention separation
interface ComputedVisibilityMetrics {
  totalChecks: number;
  mentions: number;       // Brand was mentioned (name appeared)
  citations: number;      // Link or direct recommendation (citation_quality > 0)
  mentionRate: number;
  citationRate: number;
}

interface LLMVisibilityMetrics {
  overallMentionRate: number;
  totalChecks: number;
  totalMentions: number;
  byProvider: Record<string, { checks: number; mentions: number; mentionRate: string }>;
  byQueryType: Record<string, { checks: number; mentions: number; mentionRate: string }>;
}

interface LLMVisibilityTrends {
  currentWeek: { checks: number; mentions: number; rate: string };
  previousWeek: { checks: number; mentions: number; rate: string };
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
}

interface LLMVisibilityData {
  metrics: {
    metrics: LLMVisibilityMetrics;
    trends: LLMVisibilityTrends;
  };
  latestChecks: LLMVisibilityCheck[];
  hasData: boolean;
}

interface SuccessStoryData {
  product: Product;
  conversations: ConversationsData;
  semanticGraph: SemanticGraphData;
  timeline: { events: TimelineEvent[]; totalCount: number };
  aeoMetrics: AEOMetrics;
  serpData: {
    positions: Array<{ keyword: string; position: number; tracked_at: string }>;
    overtakes: Array<{ competitor_domain: string; keyword: string }>;
    hasData: boolean;
  };
  llmVisibility?: LLMVisibilityData;
}

interface ShareLink {
  id: string;
  shareToken: string;
  shareUrl: string;
  sections: string[];
  expiresAt: string | null;
  hasPassword: boolean;
  viewCount: number;
  lastViewedAt: string | null;
  createdAt: string;
}

// Event type colors and icons (from ContentROIDashboard pattern)
const EVENT_TYPE_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  content_deployed: { color: 'bg-green-500', icon: <CheckCircle className="h-4 w-4" />, label: 'Content Deployed' },
  aeo_generated: { color: 'bg-blue-500', icon: <Sparkles className="h-4 w-4" />, label: 'AEO Generated' },
  serp_improvement: { color: 'bg-purple-500', icon: <TrendingUp className="h-4 w-4" />, label: 'SERP Improvement' },
  benchmark_improved: { color: 'bg-yellow-500', icon: <Target className="h-4 w-4" />, label: 'Benchmark Improved' },
  competitor_overtake: { color: 'bg-orange-500', icon: <Users className="h-4 w-4" />, label: 'Competitor Overtake' },
  traffic_milestone: { color: 'bg-teal-500', icon: <BarChart3 className="h-4 w-4" />, label: 'Traffic Milestone' },
};

// Platform badge colors
const PLATFORM_COLORS: Record<string, string> = {
  reddit: 'bg-orange-500',
  youtube: 'bg-red-500',
  twitter: 'bg-blue-400',
  quora: 'bg-red-600',
  medium: 'bg-gray-800',
  stackoverflow: 'bg-orange-400',
  hackernews: 'bg-orange-600',
  forum: 'bg-gray-500',
  unknown: 'bg-gray-400',
};

// AI Surface commercial names mapping
const AI_SURFACE_NAMES: Record<string, string> = {
  openai: 'ChatGPT',
  anthropic: 'Claude.ai',
  google: 'Gemini',
  perplexity: 'Perplexity',
  cohere: 'Cohere',
};

// Sentiment badge variants
const SENTIMENT_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  positive: 'default',
  neutral: 'outline',
  negative: 'destructive',
};

export default function ProductSuccessStory() {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const { selectedSite } = useSite();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SuccessStoryData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [creatingShare, setCreatingShare] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [shareExpiry, setShareExpiry] = useState<number | null>(null);
  const [selectedSections, setSelectedSections] = useState<string[]>(['all']);

  // LLM Visibility state
  const [llmVisibility, setLlmVisibility] = useState<LLMVisibilityData | null>(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [runningLlmCheck, setRunningLlmCheck] = useState(false);
  const [llmLastUpdated, setLlmLastUpdated] = useState<Date | null>(null);
  const pollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const pollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastCheckCountRef = React.useRef<number>(0);
  const stableCountRef = React.useRef<number>(0);


  // Fetch success story data
  const fetchData = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/api/success-story/${productId}`);
      if (!response.ok) {
        throw new Error(`Failed to load success story: ${response.statusText}`);
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load success story');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  // Fetch share links
  const fetchShareLinks = useCallback(async () => {
    if (!productId) return;

    try {
      const response = await authFetch(`/api/success-story/${productId}/shares`);
      if (response.ok) {
        const result = await response.json();
        setShareLinks(result.shares);
      }
    } catch (err) {
      console.error('Failed to fetch share links:', err);
    }
  }, [productId]);

  // Create share link
  const createShareLink = async () => {
    if (!productId) return;

    setCreatingShare(true);
    try {
      const response = await authFetch(`/api/success-story/${productId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sections: selectedSections,
          expiresIn: shareExpiry,
          password: sharePassword || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create share link');
      }

      const result = await response.json();

      // Copy to clipboard
      const fullUrl = `${window.location.origin}${result.shareUrl}`;
      await navigator.clipboard.writeText(fullUrl);

      // Refresh share links
      await fetchShareLinks();

      // Reset form
      setSharePassword('');
      setShareExpiry(null);
      setSelectedSections(['all']);
    } catch (err) {
      console.error('Failed to create share link:', err);
    } finally {
      setCreatingShare(false);
    }
  };

  // Delete share link
  const deleteShareLink = async (shareId: string) => {
    try {
      await authFetch(`/api/success-story/share/${shareId}`, { method: 'DELETE' });
      await fetchShareLinks();
    } catch (err) {
      console.error('Failed to delete share link:', err);
    }
  };

  // Fetch LLM visibility data
  // showLoading: only true on initial load, false during background polling
  // Returns the new check count for polling logic
  const fetchLLMVisibility = useCallback(async (showLoading = false): Promise<number> => {
    if (!productId) return 0;

    if (showLoading) {
      setLlmLoading(true);
    }
    try {
      const response = await authFetch(`/api/success-story/${productId}/llm-visibility`);
      if (response.ok) {
        const result = await response.json();
        setLlmVisibility(result);
        setLlmLastUpdated(new Date());
        return result.latestChecks?.length || 0;
      }
    } catch (err) {
      console.error('Failed to fetch LLM visibility:', err);
    } finally {
      if (showLoading) {
        setLlmLoading(false);
      }
    }
    return 0;
  }, [productId]);

  // Clear polling intervals
  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => clearPolling();
  }, [clearPolling]);

  // Trigger new LLM visibility check (runs in background)
  const triggerLLMCheck = async () => {
    if (!productId) return;

    // Clear any existing polling and reset counters
    clearPolling();
    const initialCount = llmVisibility?.latestChecks?.length || 0;
    lastCheckCountRef.current = initialCount;
    stableCountRef.current = 0;

    setRunningLlmCheck(true);
    try {
      const response = await authFetch(`/api/success-story/${productId}/llm-visibility/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          providers: ['openai', 'anthropic', 'google'],
          // Full query rotation including all difficulty levels
          queryTypes: ['brand', 'category', 'transaction', 'comparison', 'trust', 'discovery']
        })
      });

      if (response.ok) {
        // Expected checks: 3 providers × 6 query types = 18 checks
        const expectedNewChecks = 18;

        // Checks run in background - poll for results every 5 seconds
        pollIntervalRef.current = setInterval(async () => {
          const currentCount = await fetchLLMVisibility(); // Returns new count

          // Check if we've received all expected results or if results have stabilized
          const newChecksReceived = currentCount - initialCount;

          if (newChecksReceived >= expectedNewChecks) {
            // All expected checks received - stop polling
            clearPolling();
            setRunningLlmCheck(false);
          } else if (currentCount === lastCheckCountRef.current) {
            // No new results this poll
            stableCountRef.current++;
            if (stableCountRef.current >= 3) {
              // No new results for 3 consecutive polls (15 seconds) - stop polling
              clearPolling();
              setRunningLlmCheck(false);
            }
          } else {
            // Got new results, update ref and reset stable counter
            lastCheckCountRef.current = currentCount;
            stableCountRef.current = 0;
          }
        }, 5000);

        // Stop polling after 2 minutes max (safety timeout)
        pollTimeoutRef.current = setTimeout(() => {
          clearPolling();
          setRunningLlmCheck(false);
        }, 120000);

        // Also do an immediate refresh after 3 seconds
        setTimeout(async () => {
          await fetchLLMVisibility();
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to trigger LLM check:', err);
      setRunningLlmCheck(false);
    }
  };

  // Build URL for AEO Research with pre-filled keywords from this product
  const getAeoResearchUrl = () => {
    if (!data?.product) return '/research/aeo';

    // Extract keywords from category (last 2 segments) + product name
    const category = data.product.category || '';
    const categoryKeywords = category.includes('>')
      ? category.split('>').map((s: string) => s.trim()).slice(-2)
      : [category];
    const productKeywords = data.product.name.split(' ').slice(0, 3).join(' ');
    const keywords = [...categoryKeywords, productKeywords].filter(Boolean).join(', ');

    return `/research/aeo?keywords=${encodeURIComponent(keywords)}&tab=mentions`;
  };

  useEffect(() => {
    fetchData();
    fetchShareLinks();
    fetchLLMVisibility(true); // Show loading skeleton only on initial load
  }, [fetchData, fetchShareLinks, fetchLLMVisibility]);

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { product, conversations, semanticGraph, timeline, aeoMetrics, serpData } = data;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{product.name}</h1>
          <p className="text-muted-foreground">
            {product.category} | {product.siteDomain}
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Share Success Story</DialogTitle>
                <DialogDescription>
                  Create a shareable link for this product success story.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Password Protection (optional)</Label>
                  <Input
                    type="password"
                    placeholder="Leave empty for no password"
                    value={sharePassword}
                    onChange={(e) => setSharePassword(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expiration (optional)</Label>
                  <div className="flex gap-2">
                    {[null, 7, 30, 90].map((days) => (
                      <Button
                        key={days ?? 'never'}
                        size="sm"
                        variant={shareExpiry === days ? 'default' : 'outline'}
                        onClick={() => setShareExpiry(days)}
                      >
                        {days === null ? 'Never' : `${days} days`}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Existing share links */}
                {shareLinks.length > 0 && (
                  <div className="space-y-2">
                    <Label>Existing Links</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {shareLinks.map((link) => (
                        <div
                          key={link.id}
                          className="flex items-center justify-between p-2 bg-muted rounded"
                        >
                          <div className="flex-1 truncate text-sm">
                            {window.location.origin}{link.shareUrl}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{link.viewCount} views</Badge>
                            {link.hasPassword && <Badge variant="secondary">Protected</Badge>}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigator.clipboard.writeText(`${window.location.origin}${link.shareUrl}`)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteShareLink(link.id)}
                            >
                              <AlertCircle className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button onClick={createShareLink} disabled={creatingShare}>
                  {creatingShare ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  Create & Copy Link
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button variant="outline" disabled>
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>

          {product.url && (
            <Button variant="outline" asChild>
              <a href={product.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                View Product
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Conversations */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Conversations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{conversations.totalCount}</div>
            {conversations.totalCount === 0 ? (
              <Button variant="link" className="p-0 h-auto text-xs" asChild>
                <Link to={getAeoResearchUrl()}>
                  <Search className="h-3 w-3 mr-1" />
                  Find Conversations
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            ) : (
              <div className="flex gap-1 mt-2">
                {conversations.sentimentSummary && (
                  <Badge variant="default" className="text-xs">
                    {conversations.sentimentSummary.positivePercent}% positive
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Semantic Coverage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Question Coverage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {semanticGraph.summary?.answeredPercent ?? 0}%
            </div>
            <Progress
              value={semanticGraph.summary?.answeredPercent ?? 0}
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {semanticGraph.summary?.answered ?? 0} of {semanticGraph.questions?.length ?? 0} questions answered
            </p>
            {(semanticGraph.questions?.length ?? 0) === 0 && (
              <Button variant="link" className="p-0 h-auto text-xs mt-1" asChild>
                <Link to="/demand-research">
                  <FileQuestion className="h-3 w-3 mr-1" />
                  Generate FAQs
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>

        {/* AEO Score */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              AEO Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {aeoMetrics.hasAEO ? `${Math.round(aeoMetrics.benchmarkScore ?? 0)}%` : 'N/A'}
            </div>
            {aeoMetrics.hasAEO ? (
              <Badge variant="outline" className="mt-2">
                {aeoMetrics.status}
              </Badge>
            ) : (
              <Button variant="link" className="p-0 h-auto text-xs mt-1" asChild>
                <Link to="/about-business/offering">
                  <Zap className="h-3 w-3 mr-1" />
                  Generate AEO Content
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Timeline Events */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Timeline Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{timeline.totalCount}</div>
            {product.contentDeployedAt && (
              <p className="text-xs text-muted-foreground mt-1">
                Deployed {new Date(product.contentDeployedAt).toLocaleDateString()}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="conversations">
            Conversations ({conversations.totalCount})
          </TabsTrigger>
          <TabsTrigger value="semantic">Semantic Graph</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          {serpData.hasData && <TabsTrigger value="serp">SERP Tracking</TabsTrigger>}
          <TabsTrigger value="llm-visibility">
            <Bot className="h-4 w-4 mr-1" />
            AI Visibility
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sentiment Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Sentiment Analysis</CardTitle>
                <CardDescription>
                  How people talk about this product across platforms
                </CardDescription>
              </CardHeader>
              <CardContent>
                {conversations.sentimentSummary && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Positive</span>
                          <span>{conversations.sentimentSummary.positivePercent}%</span>
                        </div>
                        <Progress value={conversations.sentimentSummary.positivePercent} className="bg-green-100" />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Neutral</span>
                          <span>{conversations.sentimentSummary.neutralPercent}%</span>
                        </div>
                        <Progress value={conversations.sentimentSummary.neutralPercent} className="bg-gray-100" />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Negative</span>
                          <span>{conversations.sentimentSummary.negativePercent}%</span>
                        </div>
                        <Progress value={conversations.sentimentSummary.negativePercent} className="bg-red-100" />
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Platform Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Platform Distribution</CardTitle>
                <CardDescription>Where conversations are happening</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(conversations.platforms || {}).map(([platform, data]) => (
                    <div key={platform} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={PLATFORM_COLORS[platform] || PLATFORM_COLORS.unknown}>
                          {platform}
                        </Badge>
                      </div>
                      <span className="text-muted-foreground">{data.count} threads</span>
                    </div>
                  ))}
                  {Object.keys(conversations.platforms || {}).length === 0 && (
                    <p className="text-muted-foreground text-center py-4">
                      No conversation data available
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {timeline.events.slice(0, 5).map((event) => {
                  const config = EVENT_TYPE_CONFIG[event.event_type] || {
                    color: 'bg-gray-500',
                    icon: <Clock className="h-4 w-4" />,
                    label: event.event_type,
                  };
                  return (
                    <div key={event.id} className="flex items-start gap-4">
                      <div className={`p-2 rounded-full ${config.color} text-white`}>
                        {config.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{event.title}</div>
                        <div className="text-sm text-muted-foreground">{event.description}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(event.event_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {timeline.events.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">
                    No timeline events yet
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Conversations Tab */}
        <TabsContent value="conversations">
          <Card>
            <CardHeader>
              <CardTitle>Conversation Discovery</CardTitle>
              <CardDescription>
                Discussions about this product across platforms (from existing research data)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Platform</TableHead>
                    <TableHead>Discussion</TableHead>
                    <TableHead>Sentiment</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conversations.threads.map((thread) => (
                    <TableRow key={thread.id}>
                      <TableCell>
                        <Badge className={PLATFORM_COLORS[thread.platform] || PLATFORM_COLORS.unknown}>
                          {thread.platform}
                        </Badge>
                        {thread.source_subreddit && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {thread.source_subreddit}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <div className="truncate">{thread.text}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={SENTIMENT_VARIANTS[thread.sentiment]}>
                          {thread.sentiment}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {thread.score !== null && (
                          <span className="text-muted-foreground">{thread.score}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {thread.source_url && (
                          <Button size="sm" variant="outline" asChild>
                            <a href={thread.source_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {conversations.threads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2">
                          <MessageSquare className="h-8 w-8 text-muted-foreground" />
                          <p className="text-muted-foreground">No conversation threads found for this product</p>
                          <Button variant="outline" size="sm" asChild>
                            <Link to={getAeoResearchUrl()}>
                              <Search className="h-4 w-4 mr-2" />
                              Find Conversations
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Semantic Graph Tab */}
        <TabsContent value="semantic">
          <Card>
            <CardHeader>
              <CardTitle>Semantic Relevance Graph</CardTitle>
              <CardDescription>
                How well this product answers common questions (closer = better coverage)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SemanticGraph
                productName={product.name}
                questions={semanticGraph.questions || []}
                summary={semanticGraph.summary}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>Complete Timeline</CardTitle>
              <CardDescription>
                Full history of content changes and their impact
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {timeline.events.map((event) => {
                  const config = EVENT_TYPE_CONFIG[event.event_type] || {
                    color: 'bg-gray-500',
                    icon: <Clock className="h-4 w-4" />,
                    label: event.event_type,
                  };
                  return (
                    <div key={event.id} className="flex items-start gap-4 border-l-2 border-muted pl-4">
                      <div className={`p-2 rounded-full ${config.color} text-white shrink-0`}>
                        {config.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{event.title}</span>
                          <Badge variant="outline">{config.label}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {event.description}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                          <Calendar className="h-3 w-3" />
                          {new Date(event.event_date).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {timeline.events.length === 0 && (
                  <div className="flex flex-col items-center gap-2 py-8">
                    <Clock className="h-8 w-8 text-muted-foreground" />
                    <p className="text-muted-foreground text-center">
                      No timeline events yet. Events will appear here as content changes are tracked.
                    </p>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/about-business/offering">
                        <Zap className="h-4 w-4 mr-2" />
                        Generate AEO Content
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SERP Tab */}
        {serpData.hasData && (
          <TabsContent value="serp">
            <Card>
              <CardHeader>
                <CardTitle>SERP Position Tracking</CardTitle>
                <CardDescription>
                  Search engine ranking evolution and competitor analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Keyword</TableHead>
                      <TableHead>Position</TableHead>
                      <TableHead>Tracked</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {serpData.positions.slice(0, 20).map((pos, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{pos.keyword}</TableCell>
                        <TableCell>
                          <Badge variant={pos.position <= 3 ? 'default' : pos.position <= 10 ? 'secondary' : 'outline'}>
                            #{pos.position}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(pos.tracked_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* LLM Visibility Tab */}
        <TabsContent value="llm-visibility" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">LLM Share of Voice</h3>
              <p className="text-sm text-muted-foreground">
                Track how often AI assistants mention your product and site
              </p>
            </div>
            <div className="flex items-center gap-3">
              {runningLlmCheck ? (
                <span className="text-sm text-muted-foreground animate-pulse">
                  Running checks... results appear as they complete
                </span>
              ) : llmLastUpdated && (
                <span className="text-xs text-muted-foreground">
                  Updated {llmLastUpdated.toLocaleTimeString()}
                </span>
              )}
              <Button
                onClick={triggerLLMCheck}
                disabled={runningLlmCheck}
                variant="outline"
              >
                {runningLlmCheck ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {runningLlmCheck ? 'Checking...' : 'Run New Check'}
              </Button>
            </div>
          </div>

          {llmLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-32" />
              <Skeleton className="h-64" />
            </div>
          ) : llmVisibility?.hasData ? (
            <>
              {/* Metrics Summary - Separated Citation vs Mention */}
              {(() => {
                // Compute citation vs mention metrics from latest checks
                const checks = llmVisibility.latestChecks || [];
                const totalChecks = checks.length;
                const mentions = checks.filter(c => c.site_mentioned).length;
                const citations = checks.filter(c => c.citation_quality && c.citation_quality > 0).length;
                const mentionRate = totalChecks > 0 ? Math.round((mentions / totalChecks) * 100) : 0;
                const citationRate = totalChecks > 0 ? Math.round((citations / totalChecks) * 100) : 0;

                return (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Mention Rate - Brand Awareness */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Mention Rate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-blue-600">
                          {mentionRate}%
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {mentions} of {totalChecks} checks
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          Brand Awareness
                        </p>
                      </CardContent>
                    </Card>

                    {/* Citation Rate - Revenue Potential */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Citation Rate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                          {citationRate}%
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {citations} of {totalChecks} with links
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Revenue Potential
                        </p>
                      </CardContent>
                    </Card>

                    {/* Gap - Mentions without Citations */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Citation Gap
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-orange-600">
                          {mentions - citations}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Mentions without links
                        </p>
                        <p className="text-xs text-orange-600 mt-1">
                          Conversion opportunity
                        </p>
                      </CardContent>
                    </Card>

                    {/* Weekly Trend */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          This Week
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {llmVisibility.metrics?.trends?.currentWeek?.rate ?? 0}%
                        </div>
                        {llmVisibility.metrics?.trends && (
                          <div className="flex items-center gap-1 mt-1">
                            {llmVisibility.metrics.trends.trend === 'up' ? (
                              <TrendingUp className="h-4 w-4 text-green-500" />
                            ) : llmVisibility.metrics.trends.trend === 'down' ? (
                              <TrendingDown className="h-4 w-4 text-red-500" />
                            ) : null}
                            <span className={`text-xs ${
                              llmVisibility.metrics.trends.changePercent > 0 ? 'text-green-500' :
                              llmVisibility.metrics.trends.changePercent < 0 ? 'text-red-500' : 'text-muted-foreground'
                            }`}>
                              {llmVisibility.metrics.trends.changePercent > 0 ? '+' : ''}
                              {llmVisibility.metrics.trends.changePercent}% vs last week
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}

              {/* AI Surface Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Visibility by AI Surface</CardTitle>
                  <CardDescription>
                    How each AI assistant mentions your product
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(llmVisibility.metrics?.metrics?.byProvider ?? {}).map(([provider, data]) => (
                      <div key={provider} className="border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="h-5 w-5" />
                          <span className="font-medium">{AI_SURFACE_NAMES[provider] ?? provider}</span>
                        </div>
                        <div className="text-2xl font-bold">{data.mentionRate}%</div>
                        <Progress value={parseFloat(data.mentionRate)} className="mt-2" />
                        <p className="text-xs text-muted-foreground mt-1">
                          {data.mentions} of {data.checks} checks
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Latest Checks */}
              {/* Query Types Explanation with Strength Indicators */}
              <Card className="bg-muted/50">
                <CardContent className="py-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span>Query Types</span>
                      <span className="text-muted-foreground font-normal">• Higher difficulty = more valuable when passed</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">🏪 Brand</Badge>
                        <span className="text-green-600 text-xs">●</span>
                        <span className="text-muted-foreground">Does [site] sell this category?</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">📦 Product</Badge>
                        <span className="text-green-600 text-xs">●●</span>
                        <span className="text-muted-foreground">Tell me about [product name]</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">🏷️ Category</Badge>
                        <span className="text-yellow-600 text-xs">●●●</span>
                        <span className="text-muted-foreground">Best products in category?</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">🛒 Transaction</Badge>
                        <span className="text-yellow-600 text-xs">●●●</span>
                        <span className="text-muted-foreground">Where to buy this category?</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">⚔️ Comparison</Badge>
                        <span className="text-orange-600 text-xs">●●●●</span>
                        <span className="text-muted-foreground">Compare vs competitors</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">🛡️ Trust</Badge>
                        <span className="text-orange-600 text-xs">●●●●</span>
                        <span className="text-muted-foreground">Is brand reliable/durable?</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">🔍 Discovery</Badge>
                        <span className="text-red-600 text-xs">●●●●●</span>
                        <span className="text-muted-foreground">Find by attributes (hardest)</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Visibility Checks</CardTitle>
                  <CardDescription>
                    Latest queries to AI assistants about your product
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>AI Surface</TableHead>
                        <TableHead>Question Asked</TableHead>
                        <TableHead>Mentioned</TableHead>
                        <TableHead>Quality</TableHead>
                        <TableHead>Competitors Cited</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {llmVisibility.latestChecks?.slice(0, 10).map((check) => (
                        <TableRow key={check.id}>
                          <TableCell>
                            <Badge variant="outline">
                              {AI_SURFACE_NAMES[check.llm_provider] ?? check.llm_provider}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-md">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="capitalize text-xs">
                                  {check.query_type === 'category' ? '🏷️ Category' :
                                   check.query_type === 'product' ? '📦 Product' :
                                   check.query_type === 'brand' ? '🏪 Brand' :
                                   check.query_type === 'discovery' ? '🔍 Discovery' :
                                   check.query_type === 'transaction' ? '🛒 Transaction' :
                                   check.query_type === 'comparison' ? '⚔️ Comparison' :
                                   check.query_type === 'trust' ? '🛡️ Trust' :
                                   check.query_type === 'top5' ? '🏆 Top 5' : check.query_type}
                                </Badge>
                                <span className={`text-xs ${
                                  (check.query_strength || 1) >= 5 ? 'text-red-600' :
                                  (check.query_strength || 1) >= 4 ? 'text-orange-600' :
                                  (check.query_strength || 1) >= 3 ? 'text-yellow-600' : 'text-green-600'
                                }`}>
                                  {'●'.repeat(check.query_strength || 1)}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-2" title={check.query_text}>
                                {check.query_text}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {check.site_mentioned ? (
                              <Badge variant="default" className="bg-green-500">
                                <Eye className="h-3 w-3 mr-1" />
                                Yes
                              </Badge>
                            ) : (
                              <Badge variant="outline">No</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {check.citation_quality ? (
                              <span className="flex items-center gap-1">
                                {Array.from({ length: check.citation_quality }).map((_, i) => (
                                  <Sparkles key={i} className="h-3 w-3 text-yellow-500" />
                                ))}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            {check.competitors_mentioned && check.competitors_mentioned.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {check.competitors_mentioned.slice(0, 3).map((comp, i) => (
                                  <Badge key={i} variant="outline" className="text-xs text-orange-600 border-orange-300">
                                    {comp}
                                  </Badge>
                                ))}
                                {check.competitors_mentioned.length > 3 && (
                                  <Badge variant="outline" className="text-xs text-muted-foreground">
                                    +{check.competitors_mentioned.length - 3} more
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">None</span>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(check.checked_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!llmVisibility.latestChecks || llmVisibility.latestChecks.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                            No visibility checks yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No visibility data yet</h3>
                <p className="text-muted-foreground mb-4">
                  Click "Run New Check" to see how AI assistants mention your product
                </p>
                <Button onClick={triggerLLMCheck} disabled={runningLlmCheck}>
                  {runningLlmCheck ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Run First Check
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
